// MobileActions.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import config from '../../config';
import { 
  SECTIONS, 
  PERIODS, 
  datesFor, 
  removeDetail,
  CACHE_PREFIX
} from './utils/mobileHelpers';
import { BottomNavigation } from './components/mobile/BottomNavigation';
import { MobileActionDetail } from './components/mobile/MobileActionDetail';
import { useMobileActions } from './hooks/useMobileActions';
import './mobileActions.css';
import './mobileCockpitInsights.css';
import { MobileActionsList } from './components/mobile/MobileActionsList';

const MobileActions = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.user?.currentUser);
  const role = currentUser?.role;
  const token = currentUser?.accessToken || currentUser?.token;
  const domain = config.REACT_APP_SERVER_DOMAIN;
  
  const sectionKey = SECTIONS[searchParams.get('section')] ? searchParams.get('section') : 'mouvements';
  const section = SECTIONS[sectionKey];
  const period = PERIODS.some((item) => item.key === searchParams.get('period')) ? searchParams.get('period') : 'today';
  const search = searchParams.get('q') || '';
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10));
  // `status` sert aussi de filtre aux commandes (status=sans-livreur : commandes
  // créées jamais affectées à un livreur — le serveur ignore alors la période).
  const stockStatus = sectionKey === 'stock' ? (searchParams.get('status') || 'alert')
    : sectionKey === 'commandes' ? (searchParams.get('status') || '')
    : '';
  const detailToken = searchParams.get('detail') || '';
  
  const [draftSearch, setDraftSearch] = useState(search);
  const dates = useMemo(() => datesFor(period), [period]);
  const headers = useMemo(() => token ? { authorization: `Bearer ${token}`, token: `Bearer ${token}` } : {}, [token]);

  // Objets mémoïsés : recréés inline à chaque render, ils rendaient instable le
  // useCallback de useMobileActions et provoquaient une boucle de re-render
  // (« Maximum update depth exceeded ») via l'effet qui appelle load().
  const actionSection = useMemo(() => ({ key: sectionKey, endpoint: section.endpoint, snapshot: section.snapshot }), [sectionKey, section.endpoint, section.snapshot]);
  const actionDates = useMemo(() => ({ ...dates, period }), [dates, period]);

  const { listState, load } = useMobileActions({
    domain,
    headers,
    section: actionSection,
    dates: actionDates,
    search,
    stockStatus,
    page
  });

  const updateQuery = useCallback((patch, { replace = false, resetPage = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    if (resetPage) next.delete('page');
    navigate(`/mobile?${next.toString()}`, { replace });
  }, [navigate, searchParams]);

  useEffect(() => setDraftSearch(search), [search]);
  
  const scrollKey = useMemo(() => `${CACHE_PREFIX}scroll:${sectionKey}|${period}|${search}|${stockStatus}`, [period, search, sectionKey, stockStatus]);
  
  useEffect(() => {
    if (detailToken) return undefined;
    const saved = Number(sessionStorage.getItem(scrollKey) || 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: saved, behavior: 'instant' }));
    return () => {
      window.cancelAnimationFrame(frame);
      sessionStorage.setItem(scrollKey, String(window.scrollY));
    };
  }, [detailToken, scrollKey]);

  useEffect(() => {
    if (!detailToken) load();
  }, [detailToken, load]);

  if (role !== 'admin') return <Navigate to="/" replace />;

  const openDetail = (type, id) => {
    const next = new URLSearchParams(searchParams);
    next.set('detail', `${type}:${id}`);
    navigate(`/mobile?${next.toString()}`, { state: { from: `${location.pathname}${location.search}` } });
  };

  const backFromDetail = () => {
    if (location.state?.from) navigate(-1);
    else navigate(`/mobile?${removeDetail(searchParams).toString()}`, { replace: true });
  };

  if (detailToken) {
    return (
      <MobileActionDetail 
        detailToken={detailToken} 
        domain={domain} 
        headers={headers} 
        dates={dates} 
        onBack={backFromDetail} 
        onOpenDetail={openDetail} 
      />
    );
  }

  const meta = listState.meta || {};
  const hasMore = listState.items.length < (meta.total || 0);
  const activeNavigation = sectionKey === 'stock' ? 'stock' : sectionKey === 'mouvements' ? 'more' : 'direct';
  const searchPlaceholder = sectionKey === 'clientes' ? 'Nom ou téléphone de la cliente' : 
                           sectionKey === 'livreurs' ? 'Nom du livreur' : 
                           'Rechercher dans cette vue';
  const listTitle = sectionKey === 'mouvements' ? 'Historique récent' : 
                   sectionKey === 'stock' ? 'Combinaisons modèle · couleur · pointure' : 
                   sectionKey === 'livreurs' ? 'Performance par livreur' : 
                   'Éléments correspondants';
  const sansLivreur = sectionKey === 'commandes' && stockStatus === 'sans-livreur';
  const displaySection = sansLivreur
    ? { ...section, title: 'Commandes sans livreur', subtitle: 'Créées mais jamais affectées à un livreur — toutes périodes confondues.' }
    : section;
  const summaryText = sansLivreur ? `${meta.total || 0} commande(s) à affecter` :
                     section.snapshot ? `${meta.total || 0} ligne(s) actuellement en circulation prouvée` :
                     sectionKey === 'livreurs' ? `${meta.total || 0} livreur(s) avec activité sur ${period.toLowerCase()}` :
                     `${meta.total || 0} résultat(s) sur ${period.toLowerCase()}`;

  return (
    <section className="mobile-action-page">
      <div className="mobile-action-shell">
        <Button className="mobile-action-back" shape="circle" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/mobile?period=${period}`)} />
        
        <MobileActionsList
          sectionKey={sectionKey}
          section={displaySection}
          period={period}
          search={search}
          draftSearch={draftSearch}
          setDraftSearch={setDraftSearch}
          stockStatus={stockStatus}
          updateQuery={updateQuery}
          listState={listState}
          domain={domain}
          openDetail={openDetail}
          hasMore={hasMore}
          page={page}
          summaryText={summaryText}
          listTitle={listTitle}
          searchPlaceholder={searchPlaceholder}
          activeNavigation={activeNavigation}
        />
      </div>
      <BottomNavigation navigate={navigate} active={activeNavigation} />
    </section>
  );
};

export default MobileActions;